<template>
  <div class="row referral-campaigns-page">
    <div class="col-md-12 col-lg-12">
      <div class="card mar-bt-15">
        <div class="card-header">
          <strong>{{ $t('referralProgram.notificationEmailsTitle') }}</strong>
        </div>
        <div class="card-content row">
          <div class="col-md-12">
            <p class="text-muted">{{ $t('referralProgram.notificationEmailsHelp') }}</p>
            <textarea
              v-model="notificationEmailsRaw"
              class="form-control"
              rows="2"
              :placeholder="$t('referralProgram.notificationEmailsPlaceholder')"
            />
            <button class="btn btn-info mar-to-10" type="button" @click="saveNotificationEmails" :disabled="notificationLoading">
              {{ $t('general.save') }}
            </button>
          </div>
        </div>
      </div>

      <div class="search-bar">
        <div class="col-sm-12 no-padding">
          <div class="pull-left">
            <el-input v-model="filters[0].value" :placeholder="$t('reports.search')" class="icon-search"></el-input>
          </div>
          <div class="pull-right">
            <router-link
              class="btn add "
              :title="$t('general.add') + ' ' + $t('pages.referralCampaigns')"
              :to="{ path: '/marketing/referral-campaigns/add' }"
            >
              <img src="/static/img/icons/add.png">
              {{ $t('general.add') }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-content row">
          <!-- Same table stack as Campaigns.vue (vue-data-tables + campaign-table) so global _tables.scss applies -->
          <data-tables
            ref="referralDataTables"
            class="campaign-table width-table referral-campaigns-table"
            v-loading="loading"
            :data="tableData"
            :action-def="defaultActionDef()"
            :pagination-def="paginationDef"
            :table-props="{ border: false }"
            :filters="filters"
            @row-click="goToRecord"
            style="width: 100%"
          >
            <el-table-column
              class-name="centered-cell"
              align="center"
              header-align="center"
              :width="64"
              prop="id"
              :label="$t('customers.id')"
            />
            <el-table-column
              class-name="centered-cell"
              align="center"
              header-align="center"
              :min-width="170"
              prop="name"
              :label="$t('referralProgram.campaignName')"
            />
            <el-table-column
              class-name="centered-cell"
              align="center"
              header-align="center"
              :width="190"
              :label="$t('referralProgram.tableAvailability')"
            >
              <template slot-scope="props">
                {{ formatAvailabilityRange(props.row) }}
              </template>
            </el-table-column>
            <el-table-column
              class-name="centered-cell"
              align="center"
              header-align="center"
              :width="80"
              :label="$t('referralProgram.active')"
            >
              <template slot-scope="props">
                {{ formatActiveColumn(props.row) }}
              </template>
            </el-table-column>
            <el-table-column
              class-name="referral-campaigns-table__reward-cell"
              align="left"
              header-align="left"
              :min-width="260"
              :label="$t('referralProgram.orderRewards')"
            >
              <template slot-scope="props">
                <div v-if="rewardEntries(props.row, 'order_reward').length" class="referral-campaigns-table__rewards">
                  <div v-for="reward in rewardEntries(props.row, 'order_reward')" :key="reward.role" class="referral-campaigns-table__reward">
                    <strong>{{ reward.label }}</strong>
                    <span>{{ reward.description }}</span>
                  </div>
                </div>
                <span v-else>{{ $t('referralProgram.rewardSummaryDisabled') }}</span>
              </template>
            </el-table-column>
            <el-table-column
              class-name="referral-campaigns-table__reward-cell"
              align="left"
              header-align="left"
              :min-width="260"
              :label="$t('referralProgram.cashback')"
            >
              <template slot-scope="props">
                <div v-if="rewardEntries(props.row, 'cashback').length" class="referral-campaigns-table__rewards">
                  <div v-for="reward in rewardEntries(props.row, 'cashback')" :key="reward.role" class="referral-campaigns-table__reward">
                    <strong>{{ reward.label }}</strong>
                    <span>{{ reward.description }}</span>
                  </div>
                </div>
                <span v-else>{{ $t('referralProgram.rewardSummaryDisabled') }}</span>
              </template>
            </el-table-column>
            <el-table-column
              class-name="centered-cell"
              align="center"
              header-align="center"
              :width="120"
              prop="actions"
              :label="$t('marketingCampaigns.actions')"
            >
              <template slot-scope="props">
                <el-dropdown trigger="click" placement="bottom-end" @click.native.stop>
                  <button type="button" class="arrow-box" :aria-label="$t('marketingCampaigns.actions')" :data-row-id="props.row.id">&#9662;</button>
                  <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu referral-campaign-actions-menu">
                    <el-dropdown-item @click.native.stop="goToRecord(props.row, null, $event)">
                      {{ $t('general.edit') }}
                    </el-dropdown-item>
                    <el-dropdown-item divided @click.native.stop="handleDelete(props.$index, props.row)">
                      {{ $t('general.delete') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>

          </data-tables>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Input, Dropdown, DropdownMenu, DropdownItem } from 'element-ui'
import DataTables from 'vue-data-tables'
import swal from 'sweetalert2'
import { rewardRoles, hydrateRewardSettings, describeRule } from './referralRewardSettings'
import { attemptReferralCampaignDelete } from './referralCampaignDelete'

Vue.use(Input)
Vue.use(DataTables)

export default {
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem
  },
  props: {
    isReferralCampaigns: { type: Boolean, default: false }
  },
  watch: {
    isReferralCampaigns (v) {
      if (v) this.initPage()
    },
    tableData () {
      this.$nextTick(() => this.layoutReferralTable())
    }
  },
  data () {
    return {
      loading: false,
      notificationLoading: false,
      notificationEmailsRaw: '',
      paginationDef: {
        pageSize: 10,
        currentPage: 1,
        pageSizes: [10, 25, 50],
        show: true
      },
      filters: [{ value: '', prop: ['name', 'id'] }],
      tableData: []
    }
  },
  created () {
    this.$bus.$on('refresh', this.handleRefresh)
  },
  mounted () {
    this.initPage()
    this.loadNotificationEmails()
    this._onResizeReferralTable = () => this.layoutReferralTable()
    window.addEventListener('resize', this._onResizeReferralTable)
  },
  beforeDestroy () {
    this.$bus.$off('refresh', this.handleRefresh)
    if (this._onResizeReferralTable) {
      window.removeEventListener('resize', this._onResizeReferralTable)
    }
  },
  methods: {
    handleRefresh () {
      this.initPage()
    },
    goToRecord (row, column, event) {
      if (column && column.property === 'actions') {
        return
      }
      this.globalGoToRecord(row, event, column, '/marketing/referral-campaigns/edit/')
    },
    layoutReferralTable () {
      const dt = this.$refs.referralDataTables
      const elTable = dt && dt.$refs && dt.$refs.elTable
      if (elTable && typeof elTable.doLayout === 'function') {
        elTable.doLayout()
      }
    },
    formatAvailabilityRange (row) {
      const dash = this.$t('general.emDash')
      const from = row.datetime_validfrom || row.starts_at || row.date_start || null
      const to = row.datetime_validto || row.ends_at || row.date_end || null
      if (!from && !to) return dash
      const a = from ? this.formatDate(from) : dash
      const b = to ? this.formatDate(to) : dash
      return `${a} - ${b}`
    },
    formatActiveColumn (row) {
      const v = row.active
      if (v === undefined || v === null) return this.$t('general.yes')
      return v ? this.$t('general.yes') : this.$t('general.no')
    },
    formatBool (val) {
      return val ? this.$t('general.yes') : this.$t('general.no')
    },
    rewardEntries (row, aspect) {
      if (aspect === 'order_reward' && !row.order_rewards_active) return []
      const settings = hydrateRewardSettings(row)
      return rewardRoles.filter(role => settings[role][aspect] && settings[role][aspect].type !== 'NONE').map(role => ({
        role,
        label: this.$t('referralProgram.v2.' + role),
        description: describeRule(settings[role][aspect], this.$t.bind(this), row.reward_type)
      }))
    },
    normalizeList (response) {
      if (!response || !response.data) return []
      const d = response.data
      if (Array.isArray(d)) return d
      if (Array.isArray(d.data)) return d.data
      return []
    },
    normalizeNotificationEmailsPayload (payload) {
      if (payload == null) return []
      let p = payload
      if (typeof p === 'object' && !Array.isArray(p) && p.data != null && typeof p.data === 'object') {
        p = p.data
      }
      if (Array.isArray(p)) {
        return p.map((e) => {
          if (typeof e === 'string') return e.trim()
          if (e && typeof e === 'object') return (e.email || e.address || '').trim()
          return String(e).trim()
        }).filter(Boolean)
      }
      if (typeof p === 'string') {
        try {
          const parsed = JSON.parse(p)
          if (Array.isArray(parsed)) return this.normalizeNotificationEmailsPayload(parsed)
        } catch (_) {}
        return p.split(/[,\n]/).map((s) => s.trim()).filter(Boolean)
      }
      if (typeof p === 'object') {
        const keys = ['emails', 'referral_notification_emails', 'referralNotificationEmails', 'notification_emails']
        for (const k of keys) {
          if (p[k] != null) return this.normalizeNotificationEmailsPayload(p[k])
        }
      }
      return []
    },
    initPage () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/referral-campaign', 'GET', data, (response) => {
        if (response.status) {
          this.tableData = this.normalizeList(response)
          this.$nextTick(() => this.layoutReferralTable())
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    },
    loadNotificationEmails () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/referral-program/notification-emails', 'GET', data, (response) => {
        if (response.status && response.data != null) {
          const emails = this.normalizeNotificationEmailsPayload(response.data)
          this.notificationEmailsRaw = emails.join(', ')
        }
      })
    },
    saveNotificationEmails () {
      const parts = this.notificationEmailsRaw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean)
      this.notificationLoading = true
      const body = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        emails: parts
      }
      this.apiRequest('/referral-program/notification-emails', 'PUT', body, (response) => {
        this.notificationLoading = false
        if (response.status) {
          swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          this.loadNotificationEmails()
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
      })
    },
    handleDelete (_index, row) {
      attemptReferralCampaignDelete(this, row.id, {
        onDeleted: () => {
          this.initPage()
          swal('', this.$t('general.success'), 'success').catch(() => {})
        },
        onDeactivated: () => {
          this.initPage()
        }
      })
    }
  }
}
</script>

<style scoped>
/*
 * Global rule hides pagination inside .campaign-table (meant for Campaigns + external pager).
 * Referral uses client-side list + internal pager — show it again on this page only.
 */
.referral-campaigns-page >>> div.campaign-table div.pagination-bar {
  display: block !important;
}

</style>

<style>
/* Referral summaries span several lines; override the shared fixed-height cells here only. */
.referral-campaigns-page .referral-campaigns-table .el-table .cell {
  height: auto !important;
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.referral-campaigns-page .referral-campaigns-table .el-table__body td {
  background: #fff !important;
  vertical-align: middle;
  padding: 14px 0 !important;
  border-bottom: 10px solid #f4f6ff !important;
}

.referral-campaigns-page .referral-campaigns-table .el-table__body .cell {
  display: block !important;
  padding: 0 12px;
  background: transparent !important;
}

.referral-campaigns-page .centered-cell .cell {
  text-align: center !important;
}

.referral-campaigns-page .referral-campaigns-table__rewards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

.referral-campaigns-page .referral-campaigns-table__reward strong,
.referral-campaigns-page .referral-campaigns-table__reward span {
  display: block;
}

.referral-campaigns-page .referral-campaigns-table__reward strong {
  font-weight: 600;
}

.referral-campaigns-page .referral-campaigns-table .el-table__body tr:hover > td,
.referral-campaigns-page .referral-campaigns-table .el-table__body tr.hover-row > td {
  background-color: #e4e8f9 !important;
}

.referral-campaigns-page .referral-campaigns-table .el-table__body tr:hover > td > .cell,
.referral-campaigns-page .referral-campaigns-table .el-table__body tr.hover-row > td > .cell {
  background: transparent !important;
  transition: none;
}

/* Match the product list's compact action dropdown. */
.referral-campaigns-page .arrow-box {
  display: inline-block;
  font-size: 18px;
  line-height: normal;
  padding: 4px 6px;
  border: 1px solid #674bd2 !important;
  border-radius: 4px;
  background: transparent;
  color: #674bd2;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.referral-campaigns-page .arrow-box:hover,
.referral-campaigns-page .arrow-box:focus-visible {
  background-color: #674bd2 !important;
  border-color: #674bd2 !important;
  color: #fff !important;
}

.referral-campaign-actions-menu {
  min-width: 180px;
  font-size: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Referral/ReferralCampaigns.vue